import { Injectable } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { HttpHeaders } from '@angular/common/http'
import { BehaviorSubject, combineLatest, map, mergeMap, Observable, tap } from 'rxjs';
import { ApolloQueryResult } from '@apollo/client/core';
import { Device, DeviceCheckboxFilter, DeviceFilter, DeviceStatus, DeviceType } from '../../../models/devices';

@Injectable({
  providedIn: 'root'
})
export class DevicesService {
  private devicesSubject = new BehaviorSubject<Device[] | null>(null);
  private filterSubject = new BehaviorSubject<DeviceFilter | null>(null);
  public deviceCheckboxFilter$ = new BehaviorSubject<DeviceCheckboxFilter>({
      merchants: [],
      devices: []
  });

  public currentFilter: DeviceFilter = {};

  constructor(private apollo: Apollo) { }

  // si hay cambios en devicesSubject o filterSubject, ejecuta el pipe
  filteredDevices$: Observable<Device[]> = combineLatest([
    this.devicesSubject.asObservable(),
    this.filterSubject.asObservable()
  ]).pipe(
    map(([devices, filter]) => {
      return this.makeFilter(devices, filter);
    })
  );

  /** Carga devices desde la api graph si no están cacheados */
  private queryDevices(): void  {
    // if (this.devicesSubject.value) {
    //   return;
    // }

    const authToken = localStorage.getItem('auth.access_token');

    const headers = new HttpHeaders().set('Authorization', `Bearer ${authToken}`);

    this.apollo
      .query({
        query: gql`
          query Devices(
            $merchantId: String!
            $timeZone: String!
          ) {
            devices(
              merchant_id: $merchantId
              time_zone: $timeZone
            ) {
              items {
                merchant_id
                merchant_name
                lsn
                alias
                status
                type
                created_at
                updated_at
                created_at_time_zone
                updated_at_time_zone
              }
            }
          }
        `,
        variables: {
          merchantId: localStorage.getItem('auth.merchant_id'),
          timeZone: localStorage.getItem('auth.time_zone'),
        },
        context: {
          headers: headers,
        },
        fetchPolicy: 'network-only',
      }).pipe(
        tap((result: ApolloQueryResult<any>) => {
          if(result.data?.devices?.items) {
            console.log('graph device items!', result.data?.devices?.items);
            // llenamos el subject, con el array de dispositivos de graph
            const items: Device[] = result.data.devices.items;
            this.setDeviceCheckboxFilter(items);
            this.devicesSubject.next(items);
          } else {
            // en caso de no retornar data desde graph, entregamos un listado vacío
            this.devicesSubject.next([])
          }
        })
      )
      .subscribe();
  }

  private makeFilter(devicesArray: Device[], filter: DeviceFilter): Device[] {
    // si no hay data en devices, retornamos []
    if (!devicesArray) return [];

    // si no hay filtro, retornamos todos los devices
    if (!filter || Object.keys(filter).length == 0) return devicesArray;

    let devicesFiltered = [...devicesArray];

    //si hay filtro de input "Buscar"
    if(filter.query) {
      devicesFiltered = devicesFiltered.filter(device =>
        device.alias?.toLowerCase().includes(filter.query.toLowerCase())
        || device.merchant_name.toLowerCase().includes(filter.query.toLowerCase())
        || device.lsn.toLowerCase().includes(filter.query.toLowerCase())
      );
    }

    const { status, type, merchants, devices } = filter.checkboxes ?? {};

    //si hay filtro de checkbox de status
    if(status) {
      if(status.todos) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceStatus.ACTIVATED, DeviceStatus.DESACTIVATED, DeviceStatus.PENDING].includes(device.status));
      } else if(status.habilitado) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceStatus.ACTIVATED].includes(device.status));
      } else if(status.deshabilitado) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceStatus.DESACTIVATED, DeviceStatus.PENDING].includes(device.status));
      } else {
        devicesFiltered = devicesFiltered.filter(device => [].includes(device.status));
      }
    }

    //si hay filtro de checkbox de type
    if(type) {
      if(type.todos) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceType.SMARTPOS, DeviceType.SOFTPOS].includes(device.type));
      } else if(type.smartPOS) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceType.SMARTPOS].includes(device.type));
      } else if(type.softPOS) {
        devicesFiltered = devicesFiltered.filter(device => [DeviceType.SOFTPOS].includes(device.type));
      } else {
        devicesFiltered = devicesFiltered.filter(device => [].includes(device.type));
      }
    }

    //si hay filtro de checkbox de merchants
    if(merchants) {
      const merchantsSelected = Object.keys(merchants).filter(key => merchants[key]);
      // if(merchantsSelected.length > 0) {
        devicesFiltered = devicesFiltered.filter(device => merchantsSelected.includes(device.merchant_name));
      // }
    }

    //si hay filtro de checkbox de devices
    if(devices) {
      const devicesSelected = Object.keys(devices).filter(key => devices[key]);
      // if(devicesSelected.length > 0) {
        devicesFiltered = devicesFiltered.filter(device => devicesSelected.includes(device.alias || device.lsn));
      // }
    }

    return devicesFiltered
  }

  public listAllDevices(): Observable<Device[]> {
    this.queryDevices();
    return this.filteredDevices$;
  }

  public setFilterQuery(filterQuery: string) {
    this.currentFilter.query = filterQuery;
    this.filterSubject.next(this.currentFilter);
  }

  public clearFilter() {
    this.currentFilter = {};
    this.filterSubject.next(this.currentFilter);
  }

  private setDeviceCheckboxFilter(devices: Device[]) {
    const filterValues: DeviceCheckboxFilter = {
      merchants: [],
      devices: []
    }

    devices.forEach(device => {
      filterValues.merchants.push(device.merchant_name);
      filterValues.devices.push(device.alias || device.lsn);
    });

    filterValues.merchants = [...new Set(filterValues.merchants)].sort();
    filterValues.devices = [...new Set(filterValues.devices)].sort();

    this.deviceCheckboxFilter$.next(filterValues);
  }

  public setDeviceFilter(deviceFilter: DeviceFilter) {
    this.currentFilter = {
      ...deviceFilter,
      query: this.currentFilter.query
    };
    this.filterSubject.next(this.currentFilter);
  }
}
